import {
  type Filters,
  type GridApiExplorerStore,
  type MasterFilterType,
  useGridApiExplorerStore,
} from '@/modules/marketplace/store/grid-api-explorer-store';
import { cloneDeep, isEqual } from 'lodash-es';
import type { StatePersistor } from '@/modules/saved-views/types';
import type { FilterModel, GridState } from 'ag-grid-enterprise';

export interface OrderbookState extends Record<string, unknown> {
  selectedMasterFilter: MasterFilterType | null;
  filters: Filters;
  initialState?: GridState;
  ordersPageLimit: number;
  gridFilterModel?: FilterModel;
}

export class OrderbookStatePersistor implements StatePersistor<OrderbookState> {
  private readonly store: GridApiExplorerStore;

  public constructor() {
    this.store = useGridApiExplorerStore();
  }

  public getState(): OrderbookState {
    return {
      selectedMasterFilter: this.store.selectedMasterFilter,
      // we can't use cloneDeep here, because instances of "Decimal" or "CompanyInfo"
      // result in error when we try to call the connect endpoints
      filters: JSON.parse(JSON.stringify(this.store.filters)),
      ordersPageLimit: this.store.ordersPageLimit,

      ...(this.store.gridFilterModel === null
        ? {}
        : { gridFilterModel: this.store.gridFilterModel }),

      ...(this.store.initialState === null
        ? {}
        : {
            initialState:
              // getting rid of `undefined` fields, they don't play well with bufbuild during JSON encoding
              JSON.parse(JSON.stringify(this.store.initialState ?? {})),
          }),
    };
  }

  public setState(state: OrderbookState): void {
    this.store.selectedMasterFilter = state.selectedMasterFilter;
    this.store.filters = cloneDeep(state.filters);
    this.store.ordersPageLimit = state.ordersPageLimit;
    this.store.gridFilterModel = state.gridFilterModel ?? null;
    this.store.initialState = cloneDeep(state.initialState ?? null);
  }

  public resetState(): void {
    this.store.resetFilters();
  }

  public isDirty(state: OrderbookState): boolean {
    const currentState = cloneDeep(state);
    const savedState = cloneDeep(this.getState());

    // we don't care if column sizes have changed, since this table does auto-fit
    if (currentState.initialState) currentState.initialState.columnSizing = undefined;
    if (savedState.initialState) savedState.initialState.columnSizing = undefined;

    // if not saved, must be same as default
    // @TODO: check if this is really needed
    currentState.initialState ??= savedState.initialState;

    return !isEqual(currentState, savedState);
  }
}
